import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import './PortalPagos.css'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4003/api/v1'

function PortalPagos() {
  const navigate = useNavigate()
  const [identificacion, setIdentificacion] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [comercios, setComercios] = useState([])
  const [comercioSeleccionado, setComercioSeleccionado] = useState(null)
  const [planes, setPlanes] = useState([])
  const [planSeleccionado, setPlanSeleccionado] = useState(null)
  const [cuponCodigo, setCuponCodigo] = useState('')
  const [cuponValidado, setCuponValidado] = useState(null)
  const [calculo, setCalculo] = useState(null)
  const [paso, setPaso] = useState(1) // 1: Buscar, 2: Seleccionar Plan, 3: Confirmar

  // Cargar planes al inicio
  useEffect(() => {
    cargarPlanes()
  }, [])

  const cargarPlanes = async () => {
    try {
      const response = await fetch(`${API_URL}/pagos/planes`)
      const data = await response.json()
      if (data.success) {
        setPlanes(data.planes)
      }
    } catch (err) {
      console.error('Error cargando planes:', err)
    }
  }

  const buscarComercio = async (e) => {
    e.preventDefault()
    if (!identificacion.trim()) return

    setLoading(true)
    setError('')
    setComercios([])
    setComercioSeleccionado(null)

    try {
      const response = await fetch(`${API_URL}/pagos/buscar-comercio`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identificacion: identificacion.trim() })
      })

      const data = await response.json()

      if (data.success) {
        setComercios(data.comercios)
        if (data.comercios.length === 1) {
          seleccionarComercio(data.comercios[0])
        }
      } else {
        setError(data.message || 'No se encontraron comercios')
      }
    } catch (err) {
      setError('Error de conexión. Intente nuevamente.')
    } finally {
      setLoading(false)
    }
  }

  const seleccionarComercio = (comercio) => {
    setComercioSeleccionado(comercio)
    setPaso(2)
    // Pre-seleccionar el plan actual si tiene uno
    if (comercio.plan_actual) {
      const plan = planes.find(p => p.id === comercio.plan_actual.id)
      if (plan) {
        seleccionarPlan(plan)
      }
    }
  }

  const seleccionarPlan = async (plan) => {
    setPlanSeleccionado(plan)
    await calcularMonto(plan.id)
  }

  const calcularMonto = async (planId, cupon = cuponCodigo) => {
    if (!comercioSeleccionado) return

    try {
      const response = await fetch(`${API_URL}/pagos/calcular`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          comercio_id: comercioSeleccionado.id,
          plan_id: planId,
          cupon_codigo: cupon || null
        })
      })

      const data = await response.json()
      if (data.success) {
        setCalculo(data)
      }
    } catch (err) {
      console.error('Error calculando monto:', err)
    }
  }

  const verificarCupon = async () => {
    if (!cuponCodigo.trim()) return

    try {
      const response = await fetch(`${API_URL}/pagos/verificar-cupon`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          codigo: cuponCodigo,
          plan_id: planSeleccionado?.id,
          comercio_id: comercioSeleccionado?.id
        })
      })

      const data = await response.json()

      if (data.valido) {
        setCuponValidado(data.cupon)
        // Recalcular monto con cupón
        if (planSeleccionado) {
          await calcularMonto(planSeleccionado.id, cuponCodigo)
        }
      } else {
        setCuponValidado(null)
        setError(data.message || 'Cupón no válido')
        setTimeout(() => setError(''), 3000)
      }
    } catch (err) {
      console.error('Error verificando cupón:', err)
    }
  }

  const quitarCupon = async () => {
    setCuponCodigo('')
    setCuponValidado(null)
    if (planSeleccionado) {
      await calcularMonto(planSeleccionado.id, null)
    }
  }

  const procederPago = async () => {
    if (!comercioSeleccionado || !planSeleccionado) return

    setLoading(true)
    setError('')

    try {
      const response = await fetch(`${API_URL}/pagos/crear-transaccion`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          comercio_id: comercioSeleccionado.id,
          plan_id: planSeleccionado.id,
          cupon_codigo: cuponValidado ? cuponCodigo : null
        })
      })

      const data = await response.json()

      if (data.success) {
        // Guardar referencia para después
        sessionStorage.setItem('pago_referencia', data.referencia)
        sessionStorage.setItem('pago_comercio', comercioSeleccionado.nombre_comercial)

        // Redirigir al checkout de Wompi
        window.location.href = data.wompi.checkoutUrl
      } else {
        setError(data.message || 'Error creando la transacción')
      }
    } catch (err) {
      setError('Error de conexión. Intente nuevamente.')
    } finally {
      setLoading(false)
    }
  }

  const formatearFecha = (fecha) => {
    if (!fecha) return 'N/A'
    return new Date(fecha).toLocaleDateString('es-CO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    })
  }

  const getEstadoLicencia = (comercio) => {
    if (!comercio.licencia) return { texto: 'Sin licencia', clase: 'badge-danger' }

    const estado = comercio.licencia.estado
    const vencimiento = new Date(comercio.licencia.fecha_vencimiento)
    const hoy = new Date()
    const diasRestantes = Math.ceil((vencimiento - hoy) / (1000 * 60 * 60 * 24))

    if (estado === 'vencida' || diasRestantes < 0) {
      return { texto: 'Vencida', clase: 'badge-danger' }
    } else if (estado === 'gracia') {
      return { texto: 'En gracia', clase: 'badge-warning' }
    } else if (diasRestantes <= 7) {
      return { texto: `Vence en ${diasRestantes} días`, clase: 'badge-warning' }
    } else {
      return { texto: 'Activa', clase: 'badge-success' }
    }
  }

  return (
    <div className="portal-pagos">
      {/* Header */}
      <header className="portal-header">
        <div className="header-content">
          <h1>Posfac</h1>
          <p>Portal de Pagos</p>
        </div>
      </header>

      {/* Contenido principal */}
      <main className="portal-main">
        <div className="portal-container">

          {/* Paso 1: Buscar Comercio */}
          {paso === 1 && (
            <div className="paso-buscar">
              <div className="paso-header">
                <h2>Buscar tu comercio</h2>
                <p>Ingresa tu NIT o Cédula para buscar tu comercio y realizar el pago de tu licencia.</p>
              </div>

              <form onSubmit={buscarComercio} className="form-buscar">
                <div className="input-group">
                  <input
                    type="text"
                    value={identificacion}
                    onChange={(e) => setIdentificacion(e.target.value)}
                    placeholder="NIT o Cédula (sin dígito de verificación)"
                    className="input-identificacion"
                    autoFocus
                  />
                  <button type="submit" disabled={loading} className="btn-buscar">
                    {loading ? 'Buscando...' : 'Buscar'}
                  </button>
                </div>
                {error && <p className="error-message">{error}</p>}
              </form>

              {/* Lista de comercios encontrados */}
              {comercios.length > 1 && (
                <div className="lista-comercios">
                  <h3>Selecciona tu comercio:</h3>
                  {comercios.map(comercio => {
                    const estadoLicencia = getEstadoLicencia(comercio)
                    return (
                      <div
                        key={comercio.id}
                        className="comercio-item"
                        onClick={() => seleccionarComercio(comercio)}
                      >
                        <div className="comercio-info">
                          <strong>{comercio.nombre_comercial}</strong>
                          <span className="comercio-nit">NIT: {comercio.nit}</span>
                        </div>
                        <span className={`badge ${estadoLicencia.clase}`}>
                          {estadoLicencia.texto}
                        </span>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}

          {/* Paso 2: Seleccionar Plan */}
          {paso === 2 && comercioSeleccionado && (
            <div className="paso-plan">
              <button className="btn-volver" onClick={() => { setPaso(1); setComercioSeleccionado(null); }}>
                ← Volver
              </button>

              {/* Info del comercio */}
              <div className="comercio-seleccionado">
                <h3>{comercioSeleccionado.nombre_comercial}</h3>
                <p className="nit">NIT: {comercioSeleccionado.nit}</p>
                {comercioSeleccionado.licencia && (
                  <p className="vencimiento">
                    Licencia vence: {formatearFecha(comercioSeleccionado.licencia.fecha_vencimiento)}
                    <span className={`badge ${getEstadoLicencia(comercioSeleccionado).clase}`}>
                      {getEstadoLicencia(comercioSeleccionado).texto}
                    </span>
                  </p>
                )}
              </div>

              {/* Planes */}
              <div className="planes-container">
                <h2>Selecciona tu plan</h2>
                <div className="planes-grid">
                  {planes.map(plan => (
                    <div
                      key={plan.id}
                      className={`plan-card ${planSeleccionado?.id === plan.id ? 'selected' : ''} ${plan.es_popular ? 'popular' : ''}`}
                      onClick={() => seleccionarPlan(plan)}
                    >
                      {plan.es_popular && <div className="badge-popular">MÁS POPULAR</div>}
                      <h3>{plan.nombre}</h3>
                      <div className="plan-precio">
                        <span className="precio">{plan.precio_mostrar}</span>
                        <span className="periodo">{plan.duracion_descripcion}</span>
                      </div>
                      {plan.ahorro_descripcion && (
                        <p className="ahorro">{plan.ahorro_descripcion}</p>
                      )}
                      <p className="descripcion">{plan.descripcion}</p>
                      <ul className="caracteristicas">
                        {plan.caracteristicas?.map((c, i) => (
                          <li key={i}>✓ {c.texto}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>

              {/* Cupón */}
              {planSeleccionado && (
                <div className="cupon-container">
                  <h4>¿Tienes un cupón de descuento?</h4>
                  {!cuponValidado ? (
                    <div className="cupon-input">
                      <input
                        type="text"
                        value={cuponCodigo}
                        onChange={(e) => setCuponCodigo(e.target.value.toUpperCase())}
                        placeholder="Ingresa el código"
                      />
                      <button onClick={verificarCupon} disabled={!cuponCodigo.trim()}>
                        Aplicar
                      </button>
                    </div>
                  ) : (
                    <div className="cupon-aplicado">
                      <span className="badge badge-success">
                        ✓ {cuponValidado.codigo}: {cuponValidado.valor_descuento_formato} de descuento
                      </span>
                      <button className="btn-quitar" onClick={quitarCupon}>×</button>
                    </div>
                  )}
                </div>
              )}

              {/* Resumen del pago */}
              {calculo && planSeleccionado && (
                <div className="resumen-pago">
                  <h3>Resumen del pago</h3>
                  <div className="resumen-linea">
                    <span>{calculo.plan.nombre}</span>
                    <span>{calculo.monto_base_formato}</span>
                  </div>
                  {calculo.monto_descuento > 0 && (
                    <div className="resumen-linea descuento">
                      <span>{calculo.descuento_detalle}</span>
                      <span>{calculo.monto_descuento_formato}</span>
                    </div>
                  )}
                  <div className="resumen-linea total">
                    <span>Total a pagar</span>
                    <span>{calculo.monto_total_formato}</span>
                  </div>

                  <button
                    className="btn-pagar"
                    onClick={procederPago}
                    disabled={loading}
                  >
                    {loading ? 'Procesando...' : 'Pagar ahora'}
                  </button>

                  <p className="metodos-pago">
                    Pago seguro con: Tarjeta de crédito/débito, PSE, Nequi, Bancolombia
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="portal-footer">
        <p>Posfac © {new Date().getFullYear()} - Sistema POS</p>
        <p>Pagos procesados de forma segura por Wompi</p>
      </footer>
    </div>
  )
}

export default PortalPagos
